import { Button, Modal, Textarea } from "flowbite-react";
import { useEffect, useState } from "react";

import Loading from "../../../../common/components/ui/loading/Loading";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  getRenterById,
  updateRenterSupportJson,
} from "../../../../common/services/renters.api";

type Props = {
  renterId: null | string;
  onClose: () => void;
};

export default function ModalEditRenterSupportJson(props: Props) {
  const { renterId, onClose } = props;
  const { showToast } = useToastContext();
  const [jsonValue, setJsonValue] = useState("");
  const [renterName, setRenterName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState("");

  const notifySuccess = (text: string) => showToast({ text, type: "success" });
  const notifyError = (text: string) => showToast({ text, type: "error" });

  useEffect(() => {
    let isMounted = true;

    async function loadRenterData(renterId: string) {
      setIsLoading(true);
      setJsonError("");

      const result = await getRenterById(renterId);

      if (!result.ok || !result.data) {
        notifyError(result.message || "Não foi possível carregar o cliente.");
        onClose();
        return;
      }

      if (!isMounted) {
        return;
      }

      setRenterName(result.data.name || "");
      setJsonValue(JSON.stringify(result.data, null, 2));
      setIsLoading(false);
    }

    if (renterId) {
      loadRenterData(renterId);
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // `onClose` e `notifyError` mudam a cada render no pai/contexto e
    // causavam refetch infinito do mesmo renter ao abrir o modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renterId]);

  function formatJson() {
    try {
      const parsedValue = JSON.parse(jsonValue);
      setJsonValue(JSON.stringify(parsedValue, null, 2));
      setJsonError("");
    } catch {
      setJsonError(
        "JSON inválido. Revise a estrutura antes de formatar ou salvar.",
      );
    }
  }

  async function handleSave() {
    try {
      setIsSaving(true);
      const parsedValue = JSON.parse(jsonValue) as Record<string, unknown>;
      setJsonError("");

      const result = await updateRenterSupportJson(
        renterId as string,
        parsedValue,
      );

      if (!result.ok) {
        notifyError(
          result.message || "Não foi possível salvar o JSON do cliente.",
        );
        return;
      }

      notifySuccess("JSON do cliente atualizado com sucesso.");
      onClose();
    } catch {
      setJsonError("JSON inválido. Corrija o conteúdo antes de salvar.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      size="7xl"
      className="relative text-black dark:text-black"
      show={true}
      onClose={onClose}
    >
      <Modal.Header className="text-black dark:text-black">
        <div className="flex flex-col text-black dark:text-black">
          <span>{`Editor de suporte: ${renterName || renterId}`}</span>
          <span className="text-sm font-normal text-gray-500 text-black dark:text-black">
            Edite o JSON completo do renter. Esse fluxo permite alterar campos
            brutos do cadastro.
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className="text-black dark:text-black">
        {isLoading ? (
          <Loading />
        ) : (
          <div className="space-y-4">
            <div className=" rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Use com cuidado. Esse editor substitui o documento do cliente com
              base no JSON enviado.
            </div>

            <Textarea
              value={jsonValue}
              onChange={(event) => {
                setJsonValue(event.target.value);
                if (jsonError) {
                  setJsonError("");
                }
              }}
              rows={26}
              className="text-black dark:text-black font-mono text-xs"
            />

            {jsonError ? (
              <p className="text-sm text-red-600">{jsonError}</p>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <Button color="gray" onClick={formatJson} disabled={isSaving}>
                Formatar JSON
              </Button>
              <Button color="failure" onClick={onClose} disabled={isSaving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} isProcessing={isSaving}>
                Salvar JSON
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
